import React, { useEffect, useState } from "react";
import { UserPlus, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";

export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data.staff)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/staff", { name, email, phone, password });
      toast.success("Staff account created");
      setName(""); setEmail(""); setPhone(""); setPassword("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s, v) => {
    await api.patch(`/admin/staff/${s.id}`, { is_active: v });
    setStaff((l) => l.map((x) => (x.id === s.id ? { ...x, is_active: v } : x)));
    toast.success(v ? "Staff activated" : "Staff deactivated");
  };

  const resetPw = async (s) => {
    const pw = window.prompt(`New password for ${s.name} (min 6 chars):`);
    if (!pw) return;
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.patch(`/admin/staff/${s.id}`, { password: pw });
      toast.success("Password updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> Add staff member
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input placeholder="Full name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="staff-name" />
          <Input placeholder="Email (or leave blank)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-email" />
          <Input placeholder="Phone (or leave blank)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="staff-phone" />
          <PasswordInput placeholder="Password (min 6) *" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="staff-password" />
        </div>
        <Button
          onClick={create}
          disabled={saving || !name.trim() || (!email.trim() && !phone.trim()) || password.length < 6}
          className="mt-3 rounded-full bg-slate-900 hover:bg-black text-white"
          data-testid="staff-create-btn"
        >
          {saving ? "Creating..." : "Create staff account"}
        </Button>
        <p className="text-xs text-slate-400 mt-2">
          Staff can manage orders, products &amp; images, buyer approvals, quotes and website content. They cannot trigger Zoho sync, view Sync Health, or manage other staff.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 divide-y">
        {staff.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="staff-empty">No staff accounts yet.</div>
        )}
        {staff.map((s) => (
          <div key={s.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`staff-row-${s.id}`}>
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                {s.name}
                {s.is_active ? (
                  <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />active</span>
                ) : (
                  <span className="text-xs text-rose-500 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />deactivated</span>
                )}
              </div>
              <div className="text-xs text-slate-500">{s.email || s.phone}</div>
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => resetPw(s)} data-testid={`staff-reset-${s.id}`}>
                <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
              </Button>
              <Switch checked={s.is_active} onCheckedChange={(v) => toggle(s, v)} data-testid={`staff-active-${s.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
